<template>
	<div class="p-0 min-h-screen bg-gray-100">
		<div class="bg-white px-6 py-4 border-b border-gray-200">
			<div class="mb-2">
				<router-link :to="`/theme/${themeId}`" class="text-gray-500 no-underline text-sm inline-flex items-center hover:text-orange-7">
					<i class="fa fa-arrow-left mr-2"></i>
					Back to Theme
				</router-link>
			</div>
			<div class="flex justify-between items-center">
				<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Theme Page</h1>
				<button class="bg-orange-7 text-white border-none rounded-md px-4 py-2 text-sm font-medium cursor-pointer hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed" @click="savePage" :disabled="isSaving">
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</div>

		<div class="p-6">
			<div v-if="isLoading" class="py-12 px-6 text-center text-gray-500">Loading page...</div>

			<div v-else>
				<div class="bg-white rounded-lg shadow-sm">
					<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900 m-0">Page Properties</h2>
					</div>
					<div class="p-6">
						<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
								<input v-model="page.name" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
							</div>
							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
								<input v-model="page.type" type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
							</div>
							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Layout</label>
								<select v-model.number="page.layoutid" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border bg-white focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
									<option :value="null">None</option>
									<option v-for="layout in layouts" :key="layout.id" :value="layout.id">
										{{ layout.name || `Layout ${layout.id}` }}
									</option>
								</select>
								<button
									v-if="page.layoutid"
									class="bg-transparent border-none text-orange-7 text-xs cursor-pointer py-1 px-0 mt-1 hover:text-orange-6"
									@click="goToLayout(page.layoutid)"
								>
									Edit Layout
								</button>
							</div>
							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Template</label>
								<select v-model.number="page.templateid" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border bg-white focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
									<option :value="null">None</option>
									<option v-for="template in templates" :key="template.id" :value="template.id">
										{{ template.name || `Template ${template.id}` }}
									</option>
								</select>
								<button
									v-if="page.templateid"
									class="bg-transparent border-none text-orange-7 text-xs cursor-pointer py-1 px-0 mt-1 hover:text-orange-6"
									@click="goToTemplate(page.templateid)"
								>
									Edit Template
								</button>
							</div>
							<div class="mb-0">
								<label class="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
								<input v-model.number="page.sortorder" type="number" class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm box-border focus:outline-none focus:ring-2 focus:ring-orange-5 focus:border-transparent">
							</div>
						</div>
					</div>
				</div>

				<div class="bg-white rounded-lg shadow-sm mt-6">
					<div class="flex justify-between items-center px-6 py-4 border-b border-gray-200">
						<h2 class="text-lg font-semibold text-gray-900 m-0">Page Objects</h2>
					</div>
					<div v-if="isLoadingObjects" class="py-12 px-6 text-center text-gray-500">Loading objects...</div>
					<div v-else class="overflow-x-auto">
						<table class="w-full border-collapse">
							<thead>
								<tr>
									<th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">ID</th>
									<th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Type</th>
									<th class="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
									<th class="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Actions</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="obj in pageObjects" :key="obj.id" class="border-b border-gray-200 hover:bg-gray-50">
									<td class="px-6 py-3 text-sm text-gray-700">{{ obj.id }}</td>
									<td class="px-6 py-3 text-sm text-gray-700">{{ obj.type }}</td>
									<td class="px-6 py-3 text-sm text-gray-700">{{ obj.name || '-' }}</td>
									<td class="px-6 py-3 text-sm text-gray-700 text-right">
										<button
											class="bg-transparent border border-gray-300 rounded p-1.5 cursor-pointer text-gray-500 min-w-[32px] min-h-[32px] hover:bg-gray-100 hover:text-gray-700"
											@click="editObject(obj)"
											title="Edit"
										>
											<i class="fa fa-pencil"></i>
										</button>
									</td>
								</tr>
								<tr v-if="pageObjects.length === 0">
									<td colspan="4" class="py-12 px-6 text-center text-gray-500">No page objects found</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { httpClient } from 'utils/http';
import Swal from 'sweetalert2';

export default defineComponent({
	name: 'ThemePageEditPage',
	setup() {
		const route = useRoute();
		const router = useRouter();
		const themeId = route.params.id as string;
		const pageId = route.params.pageid as string;

		const page = ref<any>({});
		const pageObjects = ref<any[]>([]);
		const layouts = ref<any[]>([]);
		const templates = ref<any[]>([]);
		const isLoading = ref(false);
		const isLoadingObjects = ref(false);
		const isSaving = ref(false);

		const loadPage = async () => {
			isLoading.value = true;
			try {
				const { data } = await httpClient.get(`/api/page/${pageId}`);
				page.value = data;
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load page',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const loadPageObjects = async () => {
			isLoadingObjects.value = true;
			try {
				const params = new URLSearchParams({
					limit: '0',
					where: JSON.stringify({ pageid: Number(pageId) }),
				});
				const { data } = await httpClient.get(`/api/pageobject?${params}`);
				pageObjects.value = data;
			} catch (error: any) {
				console.error('Failed to load page objects:', error);
			} finally {
				isLoadingObjects.value = false;
			}
		};

		const loadReferenceData = async () => {
			try {
				const [layoutResp, templateResp] = await Promise.all([
					httpClient.get<any[]>('/api/layout?limit=0'),
					httpClient.get<any[]>('/api/template?limit=0'),
				]);
				layouts.value = layoutResp.data;
				templates.value = templateResp.data;
			} catch (error: any) {
				console.error('Failed to load reference data:', error);
			}
		};

		const savePage = async () => {
			isSaving.value = true;
			try {
				await httpClient.put(`/api/page/${pageId}`, page.value);
				Swal.fire({
					title: 'Saved',
					text: 'Page has been saved',
					icon: 'success',
					timer: 1500,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save page',
					icon: 'error',
				});
			} finally {
				isSaving.value = false;
			}
		};

		const goToLayout = (layoutId: number) => {
			router.push(`/layout/${layoutId}`);
		};

		const goToTemplate = (templateId: number) => {
			router.push(`/template/${templateId}`);
		};

		const editObject = (obj: any) => {
			router.push(`/theme/${themeId}/page/${pageId}/object/${obj.id}`);
		};

		onMounted(async () => {
			await Promise.all([loadPage(), loadPageObjects(), loadReferenceData()]);
		});

		return {
			themeId,
			page,
			pageObjects,
			layouts,
			templates,
			isLoading,
			isLoadingObjects,
			isSaving,
			savePage,
			goToLayout,
			goToTemplate,
			editObject,
		};
	},
});
</script>
